import type { RecordProxy, RecordSourceSelectorProxy } from 'relay-runtime';
import type {
	ProjectId,
	CloudId,
	ActivationId,
} from '@atlassian/jira-shared-types/src/general.tsx';
import { JiraNavigationMenuAri } from '@atlassian/ari/jira/navigation-menu';
import RelayDataID from '@atlassian/relay-data-id';

import type { MenuItemReorderOperation } from '../../../utils/constants.tsx';
import { reorder } from '../../../utils/reorder-list.tsx';

export function optimisticUpdater(
	store: RecordSourceSelectorProxy,
	operation: MenuItemReorderOperation,
	targetProjectId: ProjectId,
	cloudId: CloudId,
	activationId: ActivationId,
	relativeProjectId?: ProjectId,
) {
	// Following the format generated by JiraNavigationProjectsSidebarMenuFacade
	const assumedAri = JiraNavigationMenuAri.create({
		siteId: cloudId,
		activationId,
		navigationItemScope: 'projects',
	});

	// Relay ID format for SidebarMenu favourites query. Modify to match query if changed
	const dataID = RelayDataID({ id: assumedAri }, 'JiraProjectsSidebarMenu:favourites(first:15)');

	const favoriteConnection = store.get(`client:${dataID}`);
	if (!favoriteConnection) {
		return;
	}

	const edges = favoriteConnection.getLinkedRecords('edges') ?? [];
	if (!edges.length || edges.length <= 1) {
		return;
	}

	const projectIdToEdgeMap = edges.reduce((map, edge) => {
		const projectId = edge.getLinkedRecord('node')?.getValue('projectId')?.toString();
		return projectId ? map.set(projectId, edge) : map;
	}, new Map<ProjectId, RecordProxy>());

	const projectIds = Array.from(projectIdToEdgeMap.keys());

	const sortedProjectIds = reorder(operation, projectIds, targetProjectId, relativeProjectId);

	const sortedEdges = sortedProjectIds.map(
		(projectId) => projectIdToEdgeMap.get(projectId) ?? null,
	);

	favoriteConnection.setLinkedRecords(sortedEdges, 'edges');
}
